import styles from './ProviderLocationPlayback.module.scss';

import L from 'leaflet';
import React, { useEffect, useRef, useState } from 'react';
import { Marker, Polyline, Tooltip } from 'react-leaflet';
import moment from 'moment';
import { Point, RouteDetails } from '../../api/providers.types';
import { css } from '../../util/Css';
import { noop } from 'lodash';
import { AsyncData } from '../../redux/core';
import { MessageBar, Spinner, SpinnerSize } from '@fluentui/react';

require('leaflet.awesome-markers/dist/leaflet.awesome-markers');

const startHour = 0;
const totalTimeInMinutes = 1440;
const totalTimeInSeconds = totalTimeInMinutes * 60;

const runIcon = (L as any).AwesomeMarkers.icon({
  icon: 'Running',
  color: 'red',
  extraClasses: 'ms-Icon',
  prefix: 'ms-Icon-',
});

export const ProviderLocationPlayback = ({ location }: { location?: AsyncData<RouteDetails> }) => {
  const [playing, setPlaying] = useState(false);
  const [pegTime, setPegTime] = useState(0);
  const [pegLeft, setPegLeft] = useState(0);
  const [pegPoint, setPegPoint] = useState<Point>();
  const timelineRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (playing) {
      const begin = moment().hour(startHour).minute(0).second(0).millisecond(0);
      const current = moment(pegTime);
      let tick = current.diff(begin, 'minutes');
      const rect = timelineRef.current!.getBoundingClientRect();
      const intervalId = setInterval(() => {
        let x = (tick / totalTimeInMinutes) * rect.width;
        if (x > rect.width) {
          x = 0;
          tick = 0;
        }

        const now = new Date(pegTime);
        now.setHours(startHour, tick, 0, 0);

        const point = location!.data?.allPoints.find((p) => moment(now).format('h:mm a') === moment(p.timestamp).format('h:mm a'));

        setPegLeft(x);
        setPegTime(now.getTime());
        setPegPoint(point);

        tick++;
      }, 50);
      return () => clearInterval(intervalId);
    }
    return noop;
    // Intentional to not have pegTime in dependencies (re-entrant)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playing, location]);

  const onTrackPegMouseMove = (event: React.MouseEvent<HTMLDivElement>) => {
    setPlaying(false);

    const rect = timelineRef.current!.getBoundingClientRect();
    let x = event.clientX - rect.left;
    if (x < 0) {
      x = 0;
    }
    if (x > rect.width) {
      x = rect.width;
    }

    const now = new Date();
    now.setHours(startHour, 0, 0, 0);
    const intervalSize = totalTimeInSeconds / rect.width;
    now.setHours(startHour, 0, intervalSize * x, 0);

    const point = location!.data?.allPoints.find((p) => moment(now).format('h:mm a') === moment(p.timestamp).format('h:mm a'));
    setPegLeft(x);
    setPegTime(now.getTime());
    setPegPoint(point);
  };

  if (!location) {
    return null;
  }

  if (location?.isFetching) {
    return (
      <div className={styles.container}>
        <MessageBar>
          <Spinner size={SpinnerSize.xSmall} labelPosition="right" label="Loading provider location data..." />
        </MessageBar>
      </div>
    );
  }

  if (location.data?.allPoints.length === 0) {
    return (
      <div className={styles.container}>
        <MessageBar>No location data for this provider is present on this day.</MessageBar>
      </div>
    );
  }

  const startDate = new Date(location.data!.allPoints[0].timestamp);
  startDate.setHours(startHour, 0, 0, 0);
  return (
    <>
      <div className={styles.container}>
        <div className={styles.controls}>
          <i className={`ms-Icon ms-Icon--${playing ? 'Pause' : 'Play'}`} onClick={() => setPlaying(!playing)} />
        </div>
        <div className={styles.time}>
          <span>{moment(pegTime).format('h:mm a')}</span>
        </div>
        <div className={styles.trackContainer} ref={timelineRef} onMouseMove={onTrackPegMouseMove}>
          <div className={styles.baseline} />
          <div className={styles.track}>
            {location?.data?.segments.map((segment, index) => {
              if (segment.points.length <= 1) {
                return null;
              }
              const start = segment.points[0];
              const end = segment.points[segment.points.length - 1];
              const segmentTimeInSeconds = (end.timestamp.getTime() - start.timestamp.getTime()) / 1000;
              const classNames = css(styles.segment, {
                [styles.segmentTrip]: segment.type !== 'gap',
                [styles.segmentGap]: segment.type === 'gap',
              });

              const offsetInSeconds = (start.timestamp.getTime() - startDate.getTime()) / 1000;
              return (
                <div
                  key={index}
                  className={classNames}
                  style={{
                    left: (offsetInSeconds / totalTimeInSeconds) * 100 + '%',
                    width: (segmentTimeInSeconds / totalTimeInSeconds) * 100 + '%',
                  }}
                />
              );
            })}
          </div>
          <div className={styles.trackPeg}>
            <div className={styles.peg} style={{ left: pegLeft }}>
              <div className={styles.dot} />
            </div>
          </div>
        </div>
      </div>

      {location?.data?.segments?.map((segment, index) => {
        if (segment.type === 'gap') {
          return null;
        }
        return <Polyline className={styles.providerRoute} key={index} positions={segment.points} />;
      })}

      {pegPoint && (
        <Marker icon={runIcon} key="active-provider" position={{ lat: pegPoint.lat, lng: pegPoint.lng }}>
          <Tooltip direction="top" offset={[0, -40]} permanent>
            <div className="flex flex-column flex-center">
              <div>{moment(pegPoint.timestamp).format('h:mm a')}</div>
              <div>{pegPoint.status}</div>
            </div>
          </Tooltip>
        </Marker>
      )}
    </>
  );
};
